/**
 * Shared canvas delete for layout elements and selected simulation track nodes.
 */

import type { AppDispatch } from '../store';
import { addHistoryAction } from '../features/history/historySlice';
import {
  addElements,
  clearTrackNodeAnnotationAt,
  removeElements,
  selectTrackNode,
} from '../features/layout/layoutSlice';
import { clearSelection } from '../features/selection/selectionSlice';
import { generateId } from './geometry';
import { collectExistingEntityIds } from './entityIds';
import {
  planDeleteSimpleThroughJunction,
  planRemoveTrackNode,
} from './railInsert';
import { buildSimulationTrackNodeViews } from './simulationTrackNetworkView';
import type { LayoutElement } from '../types';
import { requestLayoutConfirmation } from '../store/layoutConfirmationStore';

export function deleteSelectedTrackNode(
  dispatch: AppDispatch,
  selectedTrackNodeId: string,
  elements: Record<string, LayoutElement>,
  equipmentInstances: Iterable<{ id: string }> = [],
  options?: { skipConfirm?: boolean }
): boolean {
  const nodes = buildSimulationTrackNodeViews(Object.values(elements));
  const node = nodes.find((n) => n.nodeId === selectedTrackNodeId);
  if (!node) {
    dispatch(selectTrackNode(null));
    return false;
  }

  const isTest = Boolean(import.meta.env?.MODE === 'test' || (globalThis as unknown as { process?: { env?: { NODE_ENV?: string } } }).process?.env?.NODE_ENV === 'test');
  if (!options?.skipConfirm && !isTest) {
    requestLayoutConfirmation({
      title: 'Confirm Delete Track Node',
      message: 'Are you sure you want to delete this track node from the layout?',
      onConfirm: () => {
        deleteSelectedTrackNode(dispatch, selectedTrackNodeId, elements, equipmentInstances, {
          skipConfirm: true,
        });
      },
    });
    return true;
  }

  const plan = planRemoveTrackNode({ x: node.x, y: node.y }, Object.values(elements), {
    existingIds: collectExistingEntityIds(Object.values(elements), equipmentInstances),
  });
  if (plan.ok) {
    const oldElements = plan.removeIds
      .map((id) => elements[id])
      .filter((el): el is LayoutElement => !!el);
    dispatch({
      type: removeElements.type,
      payload: plan.removeIds,
      meta: { skipHistory: true },
    });
    dispatch({
      type: addElements.type,
      payload: plan.addElements,
      meta: { skipHistory: true },
    });
    dispatch(
      addHistoryAction({
        id: generateId(),
        type: 'replace',
        timestamp: Date.now(),
        elementIds: plan.addElements.map((el) => el.id),
        previousState: oldElements,
        newState: plan.addElements,
      })
    );
  }

  dispatch(clearTrackNodeAnnotationAt({ x: node.x, y: node.y }));
  dispatch(selectTrackNode(null));
  dispatch(clearSelection());
  return true;
}

export function deleteSelectedLayoutElements(
  dispatch: AppDispatch,
  selectedIds: string[],
  elements: Record<string, LayoutElement>,
  options?: { skipConfirm?: boolean }
): boolean {
  if (selectedIds.length === 0) return false;

  const isTest = Boolean(import.meta.env?.MODE === 'test' || (globalThis as unknown as { process?: { env?: { NODE_ENV?: string } } }).process?.env?.NODE_ENV === 'test');
  if (!options?.skipConfirm && !isTest) {
    const count = selectedIds.length;
    const names = selectedIds
      .map((id) => elements[id]?.metadata?.name || id)
      .filter(Boolean)
      .slice(0, 3)
      .join(', ');
    const label = count === 1 ? `"${names}"` : `${count} layout items (${names}${count > 3 ? '...' : ''})`;

    requestLayoutConfirmation({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete ${label} from the layout?`,
      onConfirm: () => {
        deleteSelectedLayoutElements(dispatch, selectedIds, elements, { skipConfirm: true });
      },
    });
    return true;
  }

  // A simple through-junction is structural sugar around one continuous line.
  // Delete it as a replace so both adjacent rails are healed in the same
  // undoable transaction. Branched/ambiguous junctions use normal deletion.
  if (selectedIds.length === 1) {
    const selected = elements[selectedIds[0]];
    if (selected) {
      const plan = planDeleteSimpleThroughJunction(
        selected,
        Object.values(elements),
        { existingIds: Object.keys(elements) }
      );
      if (plan.ok) {
        const previousState = plan.removeIds
          .map((id) => elements[id])
          .filter((el): el is LayoutElement => !!el);
        dispatch({
          type: removeElements.type,
          payload: plan.removeIds,
          meta: { skipHistory: true },
        });
        dispatch({
          type: addElements.type,
          payload: plan.addElements,
          meta: { skipHistory: true },
        });
        dispatch(
          addHistoryAction({
            id: generateId(),
            type: 'replace',
            timestamp: Date.now(),
            elementIds: plan.addElements.map((el) => el.id),
            previousState,
            newState: plan.addElements,
          })
        );
        dispatch(clearSelection());
        dispatch(selectTrackNode(null));
        return true;
      }
    }
  }

  const existing = selectedIds
    .map((id) => elements[id])
    .filter((el): el is LayoutElement => !!el);
  if (existing.length > 0) {
    dispatch(
      addHistoryAction({
        id: generateId(),
        type: 'remove',
        timestamp: Date.now(),
        elementIds: existing.map((el) => el.id),
        previousState: existing,
      })
    );
  }
  dispatch(removeElements(selectedIds));
  dispatch(clearSelection());
  dispatch(selectTrackNode(null));
  return true;
}
